(() => {
  const DRAWER_SELECTOR = '[data-cart-drawer]';
  const TRIGGER_SELECTOR = '.add_to_cart_drawer';
  const OPEN_SELECTOR = '[data-cart-drawer-open]';
  const FOCUSABLE_SELECTOR = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', "[tabindex]:not([tabindex='-1'])"].join(',');

  class ShopifyCartDrawer {
    constructor(element) {
      this.element = element;
      this.panel = element.querySelector('[data-cart-drawer-panel]');
      this.notice = element.querySelector('[data-cart-added-notice]');
      this.itemsOutput = element.querySelector('[data-cart-items]');
      this.empty = element.querySelector('[data-cart-drawer-empty]');
      this.footer = element.querySelector('[data-cart-drawer-footer]');
      this.countOutput = element.querySelector('[data-cart-count]');
      this.subtotalOutput = element.querySelector('[data-cart-subtotal]');
      this.liveRegion = element.querySelector('[data-cart-live-region]');
      this.routes = window.CubeTheme?.routes || {};
      this.lastFocusedElement = null;
      this.savedBodyStyles = null;
      this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });
      this.bindEvents();
      this.refresh(false);
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest(TRIGGER_SELECTOR);
        const openTrigger = event.target.closest(OPEN_SELECTOR);
        const closeTrigger = event.target.closest('[data-cart-drawer-close]');
        const quantityTrigger = event.target.closest('[data-cart-line-key]');

        if (trigger) {
          event.preventDefault();
          const form = trigger.closest('form[action*="/cart/add"]');
          if (form) this.addFromForm(form, trigger);
          else this.addFromTrigger(trigger);
          return;
        }
        if (openTrigger) {
          event.preventDefault();
          this.refresh(false).then(() => this.open(openTrigger));
          return;
        }
        if (closeTrigger) {
          this.close();
          return;
        }
        if (quantityTrigger) {
          const key = quantityTrigger.dataset.cartLineKey;
          const quantity = Number(quantityTrigger.dataset.cartQuantity || 0);
          this.changeLine(key, quantity);
        }
      });

      document.addEventListener('submit', (event) => {
        const form = event.target.closest('form[action*="/cart/add"]');
        if (!form) return;
        const submitter = event.submitter;
        if (!submitter || !submitter.matches(TRIGGER_SELECTOR)) return;
        event.preventDefault();
        this.addFromForm(form, submitter);
      });

      document.addEventListener('keydown', (event) => {
        if (!this.isOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    async request(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json', ...(options.headers || {}) }, ...options });
      if (!response.ok) {
        let message = 'Something went wrong. Please try again.';
        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch (_) {}
        throw new Error(message);
      }
      return response.json();
    }

    async addFromTrigger(trigger) {
      const id = trigger.dataset.variantId || trigger.dataset.cartVariantId;
      if (!id) {
        this.announce('Please choose a product option first.');
        return;
      }
      trigger.setAttribute('aria-busy', 'true');
      try {
        await this.request(this.routes.cartAdd || '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(trigger.dataset.quantity || 1) }] }) });
        await this.refresh(true);
        this.open(trigger);
        this.announce('Added to your cart.');
      } catch (error) {
        this.announce(error.message);
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    }

    async addFromForm(form, trigger) {
      trigger.setAttribute('aria-busy', 'true');
      try {
        const body = new FormData(form);
        await this.request(this.routes.cartAdd || '/cart/add.js', { method: 'POST', body });
        await this.refresh(true);
        this.open(trigger);
        this.announce('Added to your cart.');
      } catch (error) {
        this.announce(error.message);
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    }

    async changeLine(key, quantity) {
      await this.request(this.routes.cartChange || '/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      await this.refresh(false);
      this.announce(quantity ? 'Cart quantity updated.' : 'Item removed from your cart.');
    }

    async refresh(showNotice) {
      const cart = await this.request(this.routes.cartJson || '/cart.js');
      this.render(cart, showNotice);
      return cart;
    }

    render(cart, showNotice = false) {
      const hasItems = cart.item_count > 0;
      this.notice.hidden = !showNotice;
      this.empty.hidden = hasItems;
      this.footer.hidden = !hasItems;
      this.countOutput.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
      this.subtotalOutput.textContent = this.formatMoney(cart.total_price);
      this.itemsOutput.innerHTML = cart.items.map((item) => this.itemTemplate(item)).join('');

      document.querySelectorAll('[data-cart-menu-count]').forEach((badge) => {
        badge.hidden = !hasItems;
        badge.textContent = String(cart.item_count);
      });
      document.querySelectorAll('[data-cart-menu-button]').forEach((button) => {
        const itemLabel = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
        button.setAttribute('aria-label', hasItems ? 'Open cart, ' + itemLabel : 'Open cart');
      });
    }

    itemTemplate(item) {
      const image = item.image ? '<img class="cart-drawer__image" src="' + item.image + '&width=180" alt="' + this.escape(item.product_title) + '">' : '';
      const minus = Math.max(0, item.quantity - 1);
      const plus = item.quantity + 1;
      return '<article class="cart-drawer__item">' +
        '<div class="cart-drawer__image-wrap">' + image + '</div>' +
        '<div class="cart-drawer__item-main"><div class="cart-drawer__item-top"><div>' +
        '<h3 class="cart-drawer__product-title">' + this.escape(item.product_title) + '</h3>' +
        '<p class="cart-drawer__product-meta">' + this.escape(item.variant_title || 'Cube Tracker product') + '</p>' +
        '</div><button class="cart-drawer__icon-button cart-drawer__remove" type="button" aria-label="Remove item" data-cart-line-key="' + this.escape(item.key) + '" data-cart-quantity="0"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg></button></div>' +
        '<div class="cart-drawer__item-bottom"><div class="cart-drawer__quantity" aria-label="Quantity"><button class="cart-drawer__quantity-button" type="button" aria-label="Decrease quantity" data-cart-line-key="' + this.escape(item.key) + '" data-cart-quantity="' + minus + '"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg></button><span class="cart-drawer__quantity-value">' + item.quantity + '</span><button class="cart-drawer__quantity-button" type="button" aria-label="Increase quantity" data-cart-line-key="' + this.escape(item.key) + '" data-cart-quantity="' + plus + '"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg></button></div><p class="cart-drawer__price">' + this.formatMoney(item.final_line_price) + '</p></div></div></article>';
    }

    formatMoney(cents) {
      return this.moneyFormatter.format((Number(cents) || 0) / 100);
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }

    open(trigger) {
      if (trigger) this.lastFocusedElement = trigger;
      if (this.isOpen()) return;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      this.savedBodyStyles = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) document.body.style.paddingRight = scrollbarWidth + 'px';
      this.element.classList.add('is-open');
      this.element.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => this.element.querySelector('[data-cart-drawer-close]')?.focus());
    }

    close() {
      if (!this.isOpen()) return;
      this.element.classList.remove('is-open');
      this.element.setAttribute('aria-hidden', 'true');
      if (this.savedBodyStyles) {
        document.body.style.overflow = this.savedBodyStyles.overflow;
        document.body.style.paddingRight = this.savedBodyStyles.paddingRight;
      }
      this.lastFocusedElement?.focus();
    }

    isOpen() { return this.element.classList.contains('is-open'); }

    trapFocus(event) {
      const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    announce(message) {
      this.liveRegion.textContent = '';
      requestAnimationFrame(() => { this.liveRegion.textContent = message; });
    }
  }

  const initialize = () => {
    const element = document.querySelector(DRAWER_SELECTOR);
    if (!element) return;
    document.querySelectorAll(TRIGGER_SELECTOR + ', ' + OPEN_SELECTOR).forEach((trigger) => {
      trigger.setAttribute('aria-controls', element.id);
      trigger.setAttribute('aria-haspopup', 'dialog');
    });
    window.CubeCartDrawer = new ShopifyCartDrawer(element);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
